import React, { useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useController } from 'react-hook-form';

import { useFormContext } from '../FormProvider';

type CodeWeight = {
  classCode: string;
  value: number;
};

interface Props {
  name: string;
  label: string;
  codeOptions: { value: string; label: string }[];
  disabled?: boolean;
}

export const ControlledCodeWeightsField: React.FC<Props> = ({ name, label, codeOptions, disabled = false }) => {
  const { control, errors } = useFormContext();
  const { field } = useController({ name, control });
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [weight, setWeight] = useState<string>('');

  const russianLettersPattern = /[А-Яа-яЁё]/;

  const items = useMemo(() => (field.value as CodeWeight[] | undefined) ?? [], [field.value]);

  const normalizedCode = selectedCode.trim();
  const isCodeValid = normalizedCode.length > 0 && !russianLettersPattern.test(normalizedCode);
  const selectedOption = useMemo(
    () => codeOptions.find((option) => option.value === normalizedCode) ?? null,
    [codeOptions, normalizedCode],
  );

  const canAdd = isCodeValid && !!weight && Number(weight) > 0;

  const onAdd = () => {
    if (!canAdd) {
      return;
    }

    const nextValue = Number(weight);
    const next = [
      ...items.filter((item) => item.classCode !== normalizedCode),
      { classCode: normalizedCode, value: nextValue },
    ].sort((left, right) => left.classCode.localeCompare(right.classCode));

    field.onChange(next);
    setWeight('');
  };

  const onRemove = (classCode: string) => {
    field.onChange(items.filter((item) => item.classCode !== classCode));
  };

  const error = errors?.[name] as { message?: string } | undefined;
  const codeInputError = selectedCode.length > 0 && !isCodeValid;

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{label}</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <Autocomplete
          freeSolo
          options={codeOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          value={selectedOption}
          inputValue={selectedCode}
          onInputChange={(_, value) => setSelectedCode(value)}
          onChange={(_, value) => {
            if (typeof value === 'string') {
              setSelectedCode(value);
              return;
            }

            setSelectedCode(value?.value ?? '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Тип инструмента"
              error={codeInputError}
              helperText={codeInputError ? 'Допустимы любые символы, кроме русских букв' : ''}
            />
          )}
          sx={{ flex: 2 }}
          disabled={disabled}
        />
        <TextField
          label="Доля, %"
          type="number"
          value={weight}
          onChange={(event) => setWeight(event.target.value)}
          disabled={disabled}
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" onClick={onAdd} disabled={!canAdd || disabled}>
          Добавить
        </Button>
      </Stack>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {items.map((item) => (
          <Chip
            key={item.classCode}
            label={`${item.classCode}: ${item.value}%`}
            onDelete={disabled ? undefined : () => onRemove(item.classCode)}
            deleteIcon={<CloseIcon />}
          />
        ))}
      </Box>
      {error?.message && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </Stack>
  );
};
